import { CSSProperties, useCallback, useRef } from "react";

import Table from "components/Table";
import TableContextProvider from "components/Table/Context";
import TableVirtualizedSpacerRow from "components/Table/VirtualizedSpacerRow";
import TableRow from "components/Table/Row";
import useTableSort from "components/Table/useTableSort";
import TableWrapper from "components/Table/Wrapper";
import EmptyState from "ds/components/EmptyState";
import { EmptystateSearchNoResultsColored } from "components/icons/generated";

import {
  ANALYTICS,
  FILTERS_ORDER_SETTINGS_KEY,
  INITIAL_COLUMNS_VIEW_CONFIG,
  initialSortDirection,
  initialSortOption,
  ROW_HEADER_ID,
  SORT_CONFIG,
  USERS_COLUMN_CONFIG,
  USERS_COLUMNS_STORAGE_KEY,
  USERS_CUSTOM_END_COLUMN_CONFIG,
} from "./constants";
import UsersListItem from "./Item";
import { UsersListUser } from "./types";
import { useSortedUsers } from "./useFilteredEntities";

type UsersListProps = {
  users: UsersListUser[];
};

const UsersList = ({ users }: UsersListProps) => {
  const virtualizedListContainerRef = useRef<HTMLDivElement | null>(null);
  const { handleSortOptionChange, sortDescriptor } = useTableSort({
    filtersOrderSettingsKey: FILTERS_ORDER_SETTINGS_KEY,
    sortConfig: SORT_CONFIG,
    initialSortOption,
    initialSortDirection,
  });

  const filteredUsers = useSortedUsers(users, sortDescriptor);

  const renderRow = useCallback(
    (
      item: Partial<UsersListUser> & {
        virtIndex?: number;
        style?: CSSProperties;
        virtKey?: string;
        id: string;
        height?: number;
        ref?: (node: Element | null | undefined) => void;
      }
    ) => {
      if ("height" in item && (item.id === "after" || item.id === "before")) {
        return <TableVirtualizedSpacerRow height={item.height} />;
      }

      return (
        <TableRow
          index={item.virtIndex}
          ref={item.ref}
          style={item.style}
          key={item.virtKey}
          id={item.id}
          name={item.username}
        >
          <UsersListItem user={item as UsersListUser} />
        </TableRow>
      );
    },
    []
  );

  return (
    <TableContextProvider
      columnsConfig={USERS_COLUMN_CONFIG}
      endCustomColumns={USERS_CUSTOM_END_COLUMN_CONFIG}
      initialColumnViewConfig={INITIAL_COLUMNS_VIEW_CONFIG}
      rowHeaderId={ROW_HEADER_ID}
      localStorageId={USERS_COLUMNS_STORAGE_KEY}
      setSortDescriptor={handleSortOptionChange}
      sortDescriptor={sortDescriptor}
      {...ANALYTICS}
    >
      <TableWrapper virtualizedListContainerRef={virtualizedListContainerRef}>
        {!filteredUsers.length && !!users?.length && (
          <EmptyState title="No results" icon={EmptystateSearchNoResultsColored} announce />
        )}

        {!!filteredUsers.length && (
          <Table ariaLabel="Roles list" items={filteredUsers} ref={virtualizedListContainerRef}>
            {renderRow}
          </Table>
        )}
      </TableWrapper>
    </TableContextProvider>
  );
};

export default UsersList;
